import { formatDate } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Observable } from 'rxjs';
import {
  CardBodyComponent,
  CardComponent,
  CardFooterAction,
  CardHeaderComponent,
  CardHeaderTitleDirective,
  GroundworksTableComponent,
  GroundworksTableData,
  StateMessageComponent,
  TableCell,
  TableColumn,
  TableRow,
} from '@groundworks/ngx-groundworks-ui';
import {
  CommissionContextDto,
  CommissionPaymentResponse,
  CommissionPaymentType,
  CommissionWithSummary,
  PpjBonusWithSummary,
  PpjEmployeeBreakdownResponse,
  PpjExceptionResponse,
  PpjPaymentResponse,
} from '../../sdk/sdk';
import { COMMISSION_PAYMENT_TYPE_LABELS } from '../../constants/commission-payment-type.constants';
import { SELECTABLE_COMMISSION_PAYMENT_TYPES } from '../../utils/selectable-commission-payment-types';
import { getUserRoles } from '../../models/roles.model';
import { ConfigService } from '../../config.service';
import { ChangeOrderEventsService } from '../../services/change-order-events.service';
import { CommissionService } from '../../services/commission.service';
import { ModalService } from '../../services/modal.service';
import { PpjService } from '../../services/ppj.service';
import { commissionErrorMessage } from '../../utils/commission-error-message';
import { phaseButtonToShow } from '../../utils/commission-rules';
import { formatUtcIsoDate, parseLocalIsoDate } from '../../utils/iso-date';
import { ppjExceptionActionMessage } from '../../utils/ppj-exception-error-message';
import { showPpjPaidButton as showPpjPaidButtonRule } from '../../utils/ppj-rules';
import { formatPercent } from '../../utils/percent';
import { PpjBudgetFormComponent } from '../ppj-budget-form/ppj-budget-form.component';

type CompensationTab = 'commissions' | 'ppj';

interface SellerPaymentEntry {
  payment: CommissionPaymentResponse;
  sourceRow: CommissionWithSummary;
}

interface SellerCommissionGroup {
  sellerId: string;
  sellerName: string;
  rows: CommissionWithSummary[];
  payments: SellerPaymentEntry[];
  paymentTotal: number;
  percentOfCommissionPaid: number | undefined;
}

interface CommissionTable {
  sellerId: string;
  data: GroundworksTableData;
}

export const PPJ_BONUS_SUMMARY_LABEL = 'Total';
// Same styleguide treatment as the "Products Total" row built by buildProductTableData.
export const PPJ_BONUS_SUMMARY_CELL_CLASS = 'fw-bold bg-light';
// Payroll and the reports call this field "Date Qualified", and the column is fed by
// PpjPaymentResponse.dateQualified — not payDate, which is the pre-payroll field the CIO forbade
// surfacing under this label. The provisional "Eligible Date" wording is gone. Held as a constant
// so the wording stays one line rather than a hunt through the markup.
export const PPJ_PAYMENT_DATE_QUALIFIED_LABEL = 'Date Qualified';

const COMMISSION_TABLE_COLUMNS: TableColumn[] = [
  { label: 'Sales Rep', dataType: 'text' },
  { label: 'Adj. Sale Amt', dataType: 'text', align: 'end' },
  { label: 'Comm. %', dataType: 'text', align: 'end' },
  { label: 'Comm. Amt', dataType: 'text', align: 'end' },
  { label: 'Pay Out', dataType: 'action-menu', align: 'center' },
  { label: 'Approved?', dataType: 'checkbox', align: 'center' },
  { label: 'Note', dataType: 'text' },
  { label: 'Edit', dataType: 'action-menu', align: 'center' },
];

const COMMISSION_PAYMENT_BAND_LABELS = [
  'Payment Type',
  PPJ_PAYMENT_DATE_QUALIFIED_LABEL,
  '% of Comm.',
  'Amount',
  '',
  'Approved?',
  'Note',
  '',
];

function rowHookClass(...hooks: string[]): string {
  return hooks.filter(Boolean).join(' ');
}

// MOD-4649 D3. Approver and approval time cannot ride on the "Approved?" cell: the library's cell
// branches are mutually exclusive and subText renders only in the standard-cell branch, so a
// checkbox cell with subText set renders nothing at all — silently. Hence eight columns, with
// Approved By carrying value + subText of its own.
//
// Amount and Date Qualified are 'text' and pre-formatted in TypeScript (T7), matching
// ppjEmployeeTableData above: the library's currency/percentage types are deliberately unused on
// this screen, and a table that formats differently from the one directly above it reads as a bug.
// Edit and Delete are 'action-menu' because that is the only dataType under which the library
// renders a cell's actionButton.
const PPJ_EXCEPTION_COLUMNS: TableColumn[] = [
  { label: 'Person', dataType: 'text' },
  { label: 'Amount', dataType: 'text', align: 'end' },
  { label: PPJ_PAYMENT_DATE_QUALIFIED_LABEL, dataType: 'text', align: 'end' },
  { label: 'Note', dataType: 'text' },
  { label: 'Approved?', dataType: 'checkbox', align: 'center' },
  { label: 'Approved By', dataType: 'text' },
  { label: 'Edit', dataType: 'action-menu', align: 'center' },
  { label: 'Delete', dataType: 'action-menu', align: 'center' },
];

@Component({
  selector: 'app-compensation-tabs',
  imports: [
    CardBodyComponent,
    CardComponent,
    CardHeaderComponent,
    CardHeaderTitleDirective,
    GroundworksTableComponent,
    PpjBudgetFormComponent,
    StateMessageComponent,
  ],
  templateUrl: './compensation-tabs.component.html',
  styleUrl: './compensation-tabs.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompensationTabsComponent {
  private readonly commissionService = inject(CommissionService);
  private readonly ppjService = inject(PpjService);
  private readonly modalService = inject(ModalService);
  private readonly configService = inject(ConfigService);
  private readonly changeOrderEvents = inject(ChangeOrderEventsService);
  private readonly destroyRef = inject(DestroyRef);

  readonly typeLabels = COMMISSION_PAYMENT_TYPE_LABELS;

  readonly projectId = input.required<string>();
  readonly branchId = input<string | null>(null);
  readonly percentProjectPaid = input<number | null>(null);
  readonly acr = input<number | null>(null);
  readonly forcedView = input<'commissions' | 'ppj' | null>(null);
  readonly enableCommissions = input<boolean>(false);
  readonly enablePpj = input<boolean>(false);
  readonly isNonInstallable = input<boolean>(false);
  readonly activeTab = signal<CompensationTab>('commissions');
  readonly view = computed(() => this.forcedView() ?? this.activeTab());

  readonly commissions = signal<CommissionWithSummary[]>([]);
  readonly commissionsError = signal(false);
  readonly commissionContext = signal<CommissionContextDto | undefined>(undefined);
  readonly commissionApproveError = signal<string | null>(null);
  readonly commissionDeleteError = signal<string | null>(null);
  readonly ppj = signal<PpjBonusWithSummary | undefined>(undefined);
  readonly ppjError = signal(false);
  readonly ppjPaidError = signal<string | null>(null);
  readonly ppjApproveError = signal<string | null>(null);
  // D11 — the exceptions card owns its own load, error and empty state. An exception never touches
  // the bonus pool, so a failure on either side must leave the other rendering its data.
  readonly ppjExceptions = signal<PpjExceptionResponse[]>([]);
  readonly ppjExceptionsError = signal(false);
  readonly ppjExceptionActionError = signal<string | null>(null);

  readonly employees = computed(() => this.ppj()?.employees ?? []);
  readonly commissionsAdjustedSale = computed(() => this.commissionContext()?.adjustedSale ?? null);
  readonly ppjAdjustedSale = computed(() => this.ppj()?.summary?.adjustedSale ?? null);
  readonly ppjActualLaborCost = computed(() => this.ppj()?.summary?.actualLaborCost ?? null);
  readonly hasPpjPayments = computed(() => (this.ppj()?.payments?.length ?? 0) > 0);
  // Read, not used: bumping it rebuilds every PPJ row and cell object. See approvalRevision below
  // for why that is the only way to undo an optimistic tick in a library cell.
  private readonly ppjApprovalRevision = signal(0);
  readonly ppjEmployeeTableData = computed<GroundworksTableData>(() => {
    this.ppjApprovalRevision();
    const employees = this.employees();
    return {
      className: 'ppj-table',
      columns: [
        { label: 'Employee Name', dataType: 'text' },
        { label: 'Hours', dataType: 'text', align: 'end' },
        { label: 'Total Labor', dataType: 'text', align: 'end' },
        { label: 'Bonus Pot', dataType: 'text', align: 'end' },
        { label: '% of Bonus', dataType: 'text', align: 'end' },
        { label: 'Bonus Amt', dataType: 'text', align: 'end' },
        { label: 'Total Pay', dataType: 'text', align: 'end' },
        { label: PPJ_PAYMENT_DATE_QUALIFIED_LABEL, dataType: 'text', align: 'end' },
        { label: 'Approved?', dataType: 'checkbox', align: 'center' },
        { label: 'Edit', dataType: 'action-menu', align: 'center' },
      ],
      rows: [
        ...employees.map((employee) => this.buildEmployeeRow(employee)),
        ...(employees.length > 0 ? [this.buildEmployeeSummaryRow(employees)] : []),
      ],
    };
  });

  // R1 — rendered in the order the server returned them. The ordering is a server-owned invariant;
  // re-sorting here would quietly override it.
  readonly ppjExceptionTableData = computed<GroundworksTableData>(() => ({
    className: 'ppj-table',
    columns: PPJ_EXCEPTION_COLUMNS,
    rows: this.ppjExceptions().map((exception) => this.buildExceptionRow(exception)),
  }));

  readonly sellerGroups = computed<SellerCommissionGroup[]>(() => this.buildSellerGroups(this.commissions()));
  // Read, not used: bumping it forces a rebuild of every row and cell object, which is the only way
  // to undo an optimistic tick in a library cell. TableCellCheckbox declares
  // `onChange: (checked: boolean) => void`, so there is no element to write back to. The library's
  // row/cell *ngFor carries no trackBy, so new object identities make Angular recreate the inputs,
  // and each one initialises from the model again. A reload alone is not enough on the error path:
  // the reloaded payment is still unapproved, so the bound value is the same false the box already
  // shows — only the revision bump forces the checkbox to be rebuilt and pick that up.
  private readonly approvalRevision = signal(0);
  readonly commissionTables = computed<CommissionTable[]>(() => {
    this.approvalRevision();
    return this.sellerGroups().map((group, index) => ({
      sellerId: group.sellerId,
      data: this.buildCommissionTableData(group, index),
    }));
  });

  // SelfGen was dropped from the commission (migration DropSelfGenFromCommissions) and now lives on
  // the job as Job.IsSelfGen, surfaced as CommissionContextDto.isSelfGen. It is job-level, so there
  // is nothing to aggregate across commission rows — one flag decides the badge for the whole
  // overview.
  //
  // Distinct from isInsideSales below, which this used to alias. Self-gen is who generated the lead;
  // inside sales is who worked the sale. Aliasing meant the Self-Gen badge inherited the
  // inside-sales rule (two different people on the sale) and so never lit for the commonest case —
  // a self-gen job sold and inspected by the same rep, which is what self-gen usually means.
  readonly isSelfGen = computed(() => this.commissionContext()?.isSelfGen === true);
  readonly isInsideSales = computed(() => this.commissionContext()?.isInsideSales === true);
  readonly anyChangeOrder = computed(() => this.commissions().some((c) => c.seller?.hasChangeOrderAttribution === true));
  readonly discount = computed<number | null>(() => this.commissionContext()?.discount ?? null);
  readonly discountPercent = computed<number | null>(() => this.commissionContext()?.discountPercent ?? null);
  readonly canWrite = computed(() => getUserRoles().CentralizedSupport);
  readonly canWriteCommissions = computed(() => this.canWrite() && this.configService.enableCommissionActions);
  readonly canWritePpj = computed(() => this.canWrite() && this.configService.enablePpjActions);
  readonly phaseButton = computed(() => phaseButtonToShow(this.commissionContext()));
  readonly commissionFooterActions = computed<CardFooterAction[]>(() => {
    const phase = this.phaseButton();
    if (phase === null || !this.canWriteCommissions()) {
      return [];
    }
    const label = phase === 'initial' ? 'Initial Commissions Paid' : 'Final Commissions Paid';
    return [{ label, type: 'primary', action: () => void this.markPhasePaid(phase) }];
  });
  readonly showPpjPaidButton = computed(
    () => showPpjPaidButtonRule(this.ppj(), this.isNonInstallable()));
  readonly ppjFooterActions = computed<CardFooterAction[]>(() => {
    if (!this.canWritePpj() || !this.showPpjPaidButton()) {
      return [];
    }
    return [{ label: 'Pay Per Job Paid', type: 'primary', action: () => void this.markPpjPaid() }];
  });

  constructor() {
    effect(() => {
      this.projectId();
      this.reloadCommissionsIfEnabled();
      if (this.enablePpj()) {
        this.loadPpj();
        this.loadPpjExceptions();
      }
    });

    // A change order rewrites the project's bound products, and the commission read path derives
    // Adjusted Sale, PAR % and Commission % from those on every read. The effect above cannot see
    // it: projectId is unchanged, so the signal never notifies. PPJ is deliberately not reloaded
    // here — out of scope for the change-order refresh story.
    this.changeOrderEvents.changeOrderAdded$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.reloadCommissionsIfEnabled(),
        error: (err: unknown) =>
          console.error('Change order stream errored; commissions may be stale until reload', err),
      });
  }

  selectTab(tab: CompensationTab): void {
    this.activeTab.set(tab);
  }

  formatCurrency(amount: number | undefined | null): string {
    if (amount == null) {
      return '—';
    }
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  }

  formatRatio(ratio: number | undefined): string {
    return formatPercent(ratio ?? undefined);
  }

  formatCommissionRatio(ratio: number | undefined): string {
    return formatPercent(ratio ?? undefined, 1);
  }

  // Two-decimal percent, shared by the commissions PAR tile and the PPJ
  // % of Bonus column (MOD-4627).
  formatRatio2(ratio: number | undefined): string {
    return formatPercent(ratio ?? undefined, 2);
  }

  formatHours(hours: number | undefined | null): string {
    if (hours == null) {
      return '—';
    }
    return hours.toFixed(2);
  }

  private roundHours(hours: number | undefined): number {
    return Number((hours ?? 0).toFixed(2));
  }

  openAddCommission(): void {
    const modalRef = this.modalService.openCommissionEditModal(
      this.projectId(), this.branchId(), null,
      this.isSelfGen(), null, this.commissionsAdjustedSale());
    this.wireCommissionEditModal(modalRef);
  }

  openEditCommission(row: CommissionWithSummary): void {
    if (!row.commission) {
      return;
    }
    const modalRef = this.modalService.openCommissionEditModal(
      this.projectId(), this.branchId(), row.commission,
      this.isSelfGen(), row.seller?.name ?? null,
      row.seller?.adjustedSale ?? this.commissionsAdjustedSale());
    this.wireCommissionEditModal(modalRef);
  }

  onCommissionSaved(): void {
    this.loadCommissions();
  }

  // Removes the whole project from the work queue for every sales rep on it, with no check that
  // anyone has actually been paid, and no undo — hence the mandatory confirmation. NgbModalRef.result
  // rejects on dismiss, so the catch is load-bearing: without it, dismissing would both mark the
  // project and throw an unhandled rejection.
  async markPhasePaid(phase: 'initial' | 'final'): Promise<void> {
    const phaseLabel = phase === 'initial' ? 'Initial Commissions' : 'Final Commissions';
    const buttonLabel = `${phaseLabel} Paid`;
    const modalRef = this.modalService.openConfirmModal(
      buttonLabel,
      `This removes the project from the ${phaseLabel} list.`,
      buttonLabel,
      'alert-warning',
      true,
      'btn-primary',
    );

    try {
      await modalRef.result;
    } catch {
      return;
    }

    const call = phase === 'initial'
      ? this.commissionService.markInitialPaid(this.projectId())
      : this.commissionService.markFinalPaid(this.projectId());

    this.commissionApproveError.set(null);
    call.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.loadCommissions(),
      error: (err: unknown) => this.commissionApproveError.set(commissionErrorMessage(err)),
    });
  }

  // Takes the whole project off the Pay Per Job queue for every crew member on it, with no check that
  // anyone has been paid, and no undo — hence the mandatory confirmation, mirroring markPhasePaid.
  // NgbModalRef.result rejects on dismiss, so the catch is load-bearing: without it, dismissing would
  // both mark the project and throw an unhandled rejection.
  async markPpjPaid(): Promise<void> {
    const buttonLabel = 'Pay Per Job Paid';
    const modalRef = this.modalService.openConfirmModal(
      buttonLabel,
      'This removes the project from the Pay Per Job list.',
      buttonLabel,
      'alert-warning',
      true,
      'btn-primary',
    );

    try {
      await modalRef.result;
    } catch {
      return;
    }

    this.ppjPaidError.set(null);
    this.ppjService
      .markPaid(this.projectId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.loadPpj(),
        error: (err: unknown) => this.ppjPaidError.set(commissionErrorMessage(err)),
      });
  }

  openPaymentModal(row: CommissionWithSummary): void {
    const modalRef = this.modalService.openCommissionPaymentModal(row);
    modalRef.componentInstance.allowedPaymentTypes.set(this.allowedPaymentTypesFor(row));
    modalRef.componentInstance.saved.subscribe(() => {
      modalRef.close();
      this.onPaymentSaved();
    });
    modalRef.componentInstance.cancelled.subscribe(() => modalRef.dismiss());
  }

  openEditPayment(row: CommissionWithSummary, payment: CommissionPaymentResponse): void {
    const modalRef = this.modalService.openCommissionPaymentModal(row);
    modalRef.componentInstance.allowedPaymentTypes.set(this.allowedPaymentTypesFor(row));
    modalRef.componentInstance.startEdit(payment);
    modalRef.componentInstance.saved.subscribe(() => {
      modalRef.close();
      this.onPaymentSaved();
    });
    modalRef.componentInstance.cancelled.subscribe(() => modalRef.dismiss());
  }

  onDeletePayment(payment: CommissionPaymentResponse): void {
    if (!payment.id || payment.approved === true) {
      return;
    }
    if (!window.confirm('Delete this payment? This cannot be undone.')) {
      return;
    }
    this.commissionDeleteError.set(null);
    this.commissionService
      .deletePayment(payment.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.loadCommissions(),
        error: (err: unknown) => this.commissionDeleteError.set(commissionErrorMessage(err)),
      });
  }

  onPaymentSaved(): void {
    this.loadCommissions();
  }

  private allowedPaymentTypesFor(_row: CommissionWithSummary): CommissionPaymentType[] {
    return SELECTABLE_COMMISSION_PAYMENT_TYPES;
  }

  // canBeApproved carries the four-eyes rule from the server (see CommissionsEndpoints.CanBeApproved),
  // which is also where it is enforced. It is already false for an approved payment.
  //
  // Async because approval is irreversible and asks first (D5). Both early exits bump
  // approvalRevision: the click has already ticked the box in the DOM, so "user said no" and "server
  // said no" need the same undo.
  async approvePayment(payment: CommissionPaymentResponse): Promise<void> {
    if (!payment.id || payment.canBeApproved !== true || !this.canWriteCommissions()) {
      this.approvalRevision.update((revision) => revision + 1);
      return;
    }
    if (!await this.confirmApproval()) {
      this.approvalRevision.update((revision) => revision + 1);
      return;
    }
    this.commissionApproveError.set(null);
    this.commissionService
      .approvePayment(payment.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.loadCommissions(),
        error: (err: unknown) => {
          this.commissionApproveError.set(commissionErrorMessage(err));
          this.approvalRevision.update((revision) => revision + 1);
        },
      });
  }

  dateInputValue(date: Date | undefined): string {
    return date == null ? '' : formatUtcIsoDate(date);
  }

  onQualificationDateChange(paymentId: string | undefined, value: string): void {
    if (!paymentId || !this.canWriteCommissions()) {
      return;
    }
    this.commissionApproveError.set(null);
    this.commissionService
      .updatePaymentDate(paymentId, parseLocalIsoDate(value) ?? null)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.loadCommissions(),
        error: (err: unknown) => this.commissionApproveError.set(commissionErrorMessage(err)),
      });
  }

  onPpjBudgetSaved(): void {
    this.loadPpj();
  }

  openForemanBonusModal(employee: PpjEmployeeBreakdownResponse): void {
    const modalRef = this.modalService.openPpjForemanBonusModal(
      this.projectId(),
      employee.userId ?? '',
      this.ppj()?.summary?.bonusPot ?? null,
      this.paymentFor(employee) ?? null,
      employee.percentOfBonus == null ? null : Math.round(employee.percentOfBonus * 100 * 100) / 100,
    );
    modalRef.componentInstance.saved.subscribe(() => {
      modalRef.close();
      this.onPpjForemanBonusSaved();
    });
    modalRef.componentInstance.cancelled.subscribe(() => modalRef.dismiss());
  }

  onPpjForemanBonusSaved(): void {
    this.loadPpj();
  }

  openAddPpjException(): void {
    this.openPpjExceptionModal(null);
  }

  openEditPpjException(exception: PpjExceptionResponse): void {
    this.openPpjExceptionModal(exception);
  }

  approvePpjException(exceptionId: string | undefined): void {
    if (!exceptionId || !this.canWritePpj()) {
      return;
    }
    this.runPpjExceptionAction(this.ppjService.approveException(exceptionId));
  }

  // Deleting an exception strips a pay adjustment off a payroll row outright, with no undo — hence
  // the mandatory confirmation, mirroring markPpjPaid.
  async deletePpjException(exceptionId: string | undefined): Promise<void> {
    if (!exceptionId || !this.canWritePpj()) {
      return;
    }
    if (!(await this.confirmExceptionDelete())) {
      return;
    }
    this.runPpjExceptionAction(this.ppjService.deleteException(exceptionId));
  }

  // NgbModalRef.result rejects on dismiss, so the catch is load-bearing: without it, dismissing would
  // both delete the exception and throw an unhandled rejection.
  private async confirmExceptionDelete(): Promise<boolean> {
    const modalRef = this.modalService.openConfirmModal(
      'Delete Exception',
      'This permanently removes the exception from this project’s Pay Per Job. This cannot be undone.',
      'Delete Exception',
    );

    try {
      await modalRef.result;
      return true;
    } catch {
      return false;
    }
  }

  // canBeApproved carries the four-eyes rule from the server (see PpjEndpoints.CanBeApproved), which
  // is also where it is enforced with a 409. It is already false for an approved payment, so it
  // subsumes the old one-way latch. One tick approves ONE payment — there is no grouped approval
  // (MOD-4680 D2), which is why this takes a payment and not an employee.
  //
  // Async because approval is irreversible and asks first (D5). Every early exit bumps
  // ppjApprovalRevision: the click has ALREADY ticked the box in the DOM, so "user said no" and
  // "server said no" need the same undo.
  async approvePpjPayment(payment: PpjPaymentResponse): Promise<void> {
    if (!payment.id || payment.canBeApproved !== true || !this.canWritePpj()) {
      this.ppjApprovalRevision.update((revision) => revision + 1);
      return;
    }
    if (!await this.confirmApproval()) {
      this.ppjApprovalRevision.update((revision) => revision + 1);
      return;
    }
    this.ppjApproveError.set(null);
    this.ppjService
      .approvePayment(payment.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.loadPpj(),
        error: (err: unknown) => {
          this.ppjApproveError.set(commissionErrorMessage(err));
          this.ppjApprovalRevision.update((revision) => revision + 1);
        },
      });
  }

  // Shared by PPJ and commissions (Task 7) — one rule, one wording. NgbModalRef.result REJECTS on
  // dismiss, so the catch is load-bearing: without it, dismissing would both approve the payment and
  // throw an unhandled rejection. Same shape as confirmExceptionDelete.
  //
  // 'btn-primary', not the openConfirmModal default of 'btn-danger': approving is the intended happy
  // path, not a destructive act. The warning it carries is that it cannot be undone.
  private async confirmApproval(): Promise<boolean> {
    const modalRef = this.modalService.openConfirmModal(
      'Approve Payment',
      'Approving is final. This payment cannot be un-approved, and it can no longer be edited or deleted.',
      'Approve Payment',
      'alert-warning',
      true,
      'btn-primary',
    );

    try {
      await modalRef.result;
      return true;
    } catch {
      return false;
    }
  }

  // Every mutation refetches, success or failure. Failure especially: a 409 means someone else
  // approved the row underneath us and a 404 means it is gone, so the list on screen is stale in
  // precisely the cases that failed, and leaving it alone would show the administrator a row that
  // no longer exists in the state they think it does.
  private runPpjExceptionAction(request$: Observable<void>): void {
    this.ppjExceptionActionError.set(null);
    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.loadPpjExceptions(),
      error: (err: unknown) => {
        this.ppjExceptionActionError.set(ppjExceptionActionMessage(err));
        this.loadPpjExceptions();
      },
    });
  }

  private openPpjExceptionModal(exception: PpjExceptionResponse | null): void {
    const modalRef = this.modalService.openPpjExceptionModal(this.projectId(), exception);
    modalRef.componentInstance.saved.subscribe(() => {
      modalRef.close();
      this.loadPpjExceptions();
    });
    modalRef.componentInstance.cancelled.subscribe(() => modalRef.dismiss());
  }

  paymentFor(employee: PpjEmployeeBreakdownResponse): PpjPaymentResponse | undefined {
    return (this.ppj()?.payments ?? []).find((payment) => payment.userId === employee.userId);
  }

  private buildEmployeeRow(employee: PpjEmployeeBreakdownResponse): TableRow {
    return {
      type: 'data',
      cells: [
        this.buildEmployeeNameCell(employee),
        { value: this.formatHours(employee.hours) },
        { value: this.formatCurrency(employee.totalLabor) },
        { value: this.formatCurrency(this.ppj()?.summary?.bonusPot ?? null) },
        { value: this.formatRatio2(employee.percentOfBonus) },
        { value: this.formatCurrency(employee.bonusAmount ?? employee.amountPaid) },
        { value: this.formatCurrency(employee.totalPay) },
        { value: this.formatDateOnly(this.paymentFor(employee)?.dateQualified) },
        this.buildEmployeeApprovedCell(employee),
        this.buildEmployeeEditCell(employee),
      ],
    };
  }

  // One payment, one tick (MOD-4680 D2). paymentFor() resolves the employee's payment the same way
  // the row's Date Qualified cell already does, so the checkbox and the date always describe the
  // same record. An employee with no payment yet gets a disabled, unchecked box.
  private buildEmployeeApprovedCell(employee: PpjEmployeeBreakdownResponse): TableCell {
    const payment = this.paymentFor(employee);
    const approved = payment?.approved === true;
    return {
      value: '',
      checkbox: {
        ariaLabel: approved ? 'Approved' : 'Not approved',
        checked: approved,
        disabled: payment?.canBeApproved !== true || !this.canWritePpj(),
        // `void` because approvePpjPayment is async and TableCellCheckbox.onChange returns void —
        // the same idiom as the exception delete action in buildExceptionDeleteCell.
        onChange: () => { if (payment) void this.approvePpjPayment(payment); },
      },
    };
  }

  // The cell stays in place when the pencil is gated away, so the Edit column keeps the employee
  // table on the same nine-column grid as its totals row.
  //
  // Gated on the bonus row as well as the role (MOD-4677). The table now renders before a pot is
  // entered, but the modal behind this pencil POSTs to /ppj/project/{id}/payments, which 404s while
  // there is no bonus row to hang a payment off. Offering a control that cannot succeed is worse
  // than withholding it; saving a pot refetches and brings the pencil back on the same repaint.
  private buildEmployeeEditCell(employee: PpjEmployeeBreakdownResponse): TableCell {
    if (!this.canWritePpj() || this.ppj()?.bonus == null) {
      return { value: '' };
    }
    return {
      value: '',
      actionButton: {
        icon: 'bi bi-pencil',
        ariaLabel: 'Edit foreman bonus',
        variant: 'secondary',
        action: () => this.openForemanBonusModal(employee),
      },
    };
  }

  // The type carries under the name so the percentages can be apportioned by role in context.
  // `||` not `??` on purpose: a blank or whitespace-only type must fall back to the em dash too,
  // otherwise an upstream ETL writing '' would hang an empty muted line under every name.
  private buildEmployeeNameCell(employee: PpjEmployeeBreakdownResponse): TableCell {
    return {
      value: employee.name ?? employee.userId ?? '—',
      subText: employee.employeeType?.trim() || '—',
      className: 'text-nowrap',
    };
  }

  // Bonus Pot is a job-level figure repeated on every employee row, so the summary echoes it rather
  // than summing. Percentages share that one pot as their denominator, so the raw ratios are summed
  // and formatted once — adding the already-rounded per-row values would drift. The total is not
  // expected to reach 100%: percentages are independent and nothing is redistributed, so three
  // installers total 130% and a crew with no co-foreman totals 90% (MOD-4627). Date Qualified,
  // Approved? and Edit do not aggregate, so they stay empty.
  private buildEmployeeSummaryRow(employees: PpjEmployeeBreakdownResponse[]): TableRow {
    const sumOf = (select: (employee: PpjEmployeeBreakdownResponse) => number | undefined): number =>
      employees.reduce((running, employee) => running + (select(employee) ?? 0), 0);

    const values = [
      PPJ_BONUS_SUMMARY_LABEL,
      this.formatHours(sumOf((employee) => this.roundHours(employee.hours))),
      this.formatCurrency(sumOf((employee) => employee.totalLabor)),
      this.formatCurrency(this.ppj()?.summary?.bonusPot ?? null),
      this.formatRatio2(sumOf((employee) => employee.percentOfBonus)),
      this.formatCurrency(sumOf((employee) => employee.bonusAmount ?? employee.amountPaid)),
      this.formatCurrency(sumOf((employee) => employee.totalPay)),
      '',
      '',
      '',
    ];

    return {
      type: 'total',
      cells: values.map((value) => ({ value, isHeader: true, className: PPJ_BONUS_SUMMARY_CELL_CLASS })),
    };
  }

  private buildCommissionTableData(group: SellerCommissionGroup, groupIndex: number): GroundworksTableData {
    return {
      className: 'commission-table',
      caption: `Commissions and payments for ${group.sellerName}`,
      columns: COMMISSION_TABLE_COLUMNS,
      rows: [
        ...group.rows.map((row) => this.buildCommissionSummaryRow(group, row, groupIndex)),
        ...this.buildPaymentBand(group),
      ],
    };
  }

  private buildPaymentBand(group: SellerCommissionGroup): TableRow[] {
    if (group.payments.length === 0) {
      return [];
    }
    return [
      this.buildPaymentHeaderRow(group),
      ...group.payments.map((entry) => this.buildPaymentRow(entry)),
      this.buildPaymentTotalRow(group),
    ];
  }

  private buildCommissionSummaryRow(
    group: SellerCommissionGroup,
    row: CommissionWithSummary,
    groupIndex: number,
  ): TableRow {
    const id = row.commission?.id ?? '';
    const hook = rowHookClass(`commission-summary-row-${id}`, groupIndex % 2 === 1 ? 'commission-row-alt' : '');
    return {
      type: 'data',
      cells: [
        { value: group.sellerName, className: rowHookClass(hook, `sales-rep-name-${id}`) },
        { value: this.formatCurrency(row.seller?.adjustedSale), className: hook },
        { value: this.formatCommissionRatio(row.seller?.commissionRatio), className: hook },
        { value: this.formatCurrency(row.commission?.amount), className: hook },
        this.buildPayOutCell(row, hook),
        { value: '', className: hook },
        { value: '', className: hook },
        this.buildCommissionEditCell(row, hook),
      ],
    };
  }

  // D2 — TableCell carries no row id, so the exception this row was built from is captured in the
  // onChange/action closures below. There is no way to read it back off a cell afterwards.
  private buildExceptionRow(exception: PpjExceptionResponse): TableRow {
    return {
      type: 'data',
      cells: [
        { value: exception.userDisplayName ?? '—', className: 'text-nowrap' },
        { value: this.formatCurrency(exception.amount) },
        { value: this.formatDateOnly(exception.dateQualified) },
        { value: exception.note?.trim() || '—' },
        this.buildExceptionApprovedCell(exception),
        this.buildExceptionApproverCell(exception),
        this.buildExceptionEditCell(exception),
        this.buildExceptionDeleteCell(exception),
      ],
    };
  }

  private buildPayOutCell(row: CommissionWithSummary, hook: string): TableCell {
    if (!this.canWriteCommissions()) {
      return { value: '', className: hook };
    }
    return {
      value: '',
      className: rowHookClass(hook, `commission-pay-out-${row.commission?.id ?? ''}`),
      actionButton: {
        label: 'Pay Out',
        ariaLabel: 'Pay Out',
        variant: 'primary',
        action: () => this.openPaymentModal(row),
      },
    };
  }

  // Approval is a one-way latch, so an approved row's checkbox is inert rather than a toggle.
  private buildExceptionApprovedCell(exception: PpjExceptionResponse): TableCell {
    const approved = exception.isApproved === true;
    return {
      value: '',
      checkbox: {
        ariaLabel: 'Approved',
        checked: approved,
        disabled: approved || !this.canWritePpj(),
        onChange: () => this.approvePpjException(exception.id),
      },
    };
  }

  private buildCommissionEditCell(row: CommissionWithSummary, hook: string): TableCell {
    if (!this.canWriteCommissions()) {
      return { value: '', className: hook };
    }
    return {
      value: '',
      className: rowHookClass(hook, `commission-edit-${row.commission?.id ?? ''}`),
      actionButton: {
        icon: 'bi bi-pencil',
        ariaLabel: 'Edit commission',
        variant: 'secondary',
        action: () => this.openEditCommission(row),
      },
    };
  }

  // R5 — the approver is resolved to a display name server-side; the raw id never reaches a cell.
  private buildExceptionApproverCell(exception: PpjExceptionResponse): TableCell {
    if (exception.isApproved !== true) {
      return { value: '—' };
    }
    return {
      value: exception.approvedByDisplayName ?? '—',
      subText: this.formatApprovalStamp(exception.approvedAt),
    };
  }

  // D12 — gated away means ABSENT, not disabled: a disabled button still passes the spec's sweep
  // over every write control on the tab. The cell itself stays so the row keeps its eight columns,
  // matching buildEmployeeEditCell.
  private buildExceptionEditCell(exception: PpjExceptionResponse): TableCell {
    if (!this.canWritePpj() || exception.isApproved === true) {
      return { value: '' };
    }
    return {
      value: '',
      actionButton: {
        icon: 'bi bi-pencil',
        ariaLabel: 'Edit exception',
        variant: 'secondary',
        action: () => this.openEditPpjException(exception),
      },
    };
  }

  private buildPaymentHeaderRow(group: SellerCommissionGroup): TableRow {
    const hook = `payment-header-row-${group.sellerId}`;
    return {
      type: 'data',
      cells: COMMISSION_PAYMENT_BAND_LABELS.map((label) => ({
        value: label,
        isHeader: true,
        scope: 'col' as const,
        className: hook,
      })),
    };
  }

  private buildPaymentRow(entry: SellerPaymentEntry): TableRow {
    const payment = entry.payment;
    const id = payment.id ?? '';
    const hook = `payment-row-${id}`;
    const percentOfComm = this.percentOfCommission(payment.amount, entry.sourceRow.commission?.amount);
    return {
      type: 'data',
      cells: [
        { value: payment.type !== undefined ? this.typeLabels[payment.type] : '—', className: hook },
        this.buildQualificationDateCell(payment, hook),
        { value: this.formatCommissionRatio(percentOfComm), className: hook },
        { value: this.formatCurrency(payment.amount), className: hook },
        { value: '', className: hook },
        this.buildPaymentApprovedCell(payment, hook),
        { value: payment.notes || '—', className: rowHookClass(hook, `payment-note-${id}`) },
        this.buildPaymentActionsCell(entry, hook),
      ],
    };
  }

  private buildQualificationDateCell(payment: CommissionPaymentResponse, hook: string): TableCell {
    const id = payment.id ?? '';
    const dateHook = rowHookClass(hook, `payment-date-${id}`);
    if (payment.approved === true) {
      return { value: this.formatDisplayDate(payment.qualificationDate), className: dateHook };
    }
    return {
      value: '',
      className: dateHook,
      input: {
        type: 'date',
        defaultValue: this.dateInputValue(payment.qualificationDate),
        ariaLabel: 'Date Qualified',
        disabled: !this.canWriteCommissions(),
        onChange: (value) => this.onQualificationDateChange(payment.id, String(value)),
      },
    };
  }

  private buildExceptionDeleteCell(exception: PpjExceptionResponse): TableCell {
    if (!this.canWritePpj() || exception.isApproved === true) {
      return { value: '' };
    }
    return {
      value: '',
      actionButton: {
        icon: 'bi bi-trash',
        ariaLabel: 'Delete exception',
        variant: 'danger',
        action: () => void this.deletePpjException(exception.id),
      },
    };
  }

  // Gated on canBeApproved, not on `approved`: two-step approval means a payment that is merely
  // unapproved is not yet approvable, and canBeApproved is already false once approved, so it
  // subsumes the old one-way latch.
  private buildPaymentApprovedCell(payment: CommissionPaymentResponse, hook: string): TableCell {
    const approved = payment.approved === true;
    return {
      value: '',
      className: rowHookClass(hook, `payment-approved-indicator-${payment.id ?? ''}`),
      checkbox: {
        ariaLabel: approved ? 'Approved' : 'Not approved',
        checked: approved,
        disabled: payment.canBeApproved !== true || !this.canWriteCommissions(),
        onChange: () => void this.approvePayment(payment),
      },
    };
  }

  private buildPaymentActionsCell(entry: SellerPaymentEntry, hook: string): TableCell {
    const payment = entry.payment;
    if (!this.canWriteCommissions() || payment.approved === true) {
      return { value: '', className: hook };
    }
    return {
      value: '',
      className: rowHookClass(hook, `payment-actions-${payment.id ?? ''}`),
      actionMenu: {
        rowTitle: this.paymentMenuTitle(payment),
        actions: [
          { label: 'Edit', action: () => this.openEditPayment(entry.sourceRow, payment) },
          { label: 'Delete', action: () => this.onDeletePayment(payment) },
        ],
      },
    };
  }

  private paymentMenuTitle(payment: CommissionPaymentResponse): string {
    const type = payment.type !== undefined ? this.typeLabels[payment.type] : 'commission';
    return `${type} payment`;
  }

  private buildPaymentTotalRow(group: SellerCommissionGroup): TableRow {
    const cells: { value: string; hook?: string }[] = [
      { value: PPJ_BONUS_SUMMARY_LABEL },
      { value: '' },
      {
        value: this.formatCommissionRatio(group.percentOfCommissionPaid),
        hook: `payment-total-percent-${group.sellerId}`,
      },
      { value: this.formatCurrency(group.paymentTotal), hook: `payment-total-amount-${group.sellerId}` },
      { value: '' },
      { value: '' },
      { value: '' },
      { value: '' },
    ];
    const rowHook = `payment-total-row-${group.sellerId}`;

    return {
      type: 'total',
      cells: cells.map(({ value, hook }) => ({
        value,
        isHeader: true,
        className: rowHookClass(rowHook, hook ?? '', PPJ_BONUS_SUMMARY_CELL_CLASS),
      })),
    };
  }

  private buildSellerGroups(rows: CommissionWithSummary[]): SellerCommissionGroup[] {
    const groupsBySellerId = new Map<string, SellerCommissionGroup>();

    for (const row of rows) {
      const sellerId = row.commission?.userId ?? row.commission?.id ?? 'unknown';
      const existing = groupsBySellerId.get(sellerId);
      if (existing) {
        existing.rows.push(row);
        continue;
      }
      groupsBySellerId.set(sellerId, {
        sellerId,
        sellerName: row.seller?.name ?? row.commission?.userId ?? '—',
        rows: [row],
        payments: [],
        paymentTotal: 0,
        percentOfCommissionPaid: undefined,
      });
    }

    return Array.from(groupsBySellerId.values()).map((group) => {
      const payments = this.buildSellerPayments(group.rows);
      return { ...group, payments, ...this.buildSellerPaymentTotals(group.rows, payments) };
    });
  }

  // A rep can hold several commissions in one block (self-gen + change order), and each payment's
  // "% of Comm." is measured against its own commission. Adding those percentages would mix
  // denominators, so the total is recomputed as total paid over total commission.
  //
  // The ratio is deliberately unclamped above 100% — a best-guess payout that overshoots the final
  // commission is real, and surfacing it is the point of the row. It is suppressed entirely for a
  // non-positive base: clawbacks and deductions leave reps whose commissions net to zero or below,
  // and a percentage of a zero or negative base is not meaningful (a -$50 base against $1,133.16
  // paid renders -2,266%). The amount total still shows what was paid.
  private buildSellerPaymentTotals(
    rows: CommissionWithSummary[],
    payments: SellerPaymentEntry[],
  ): Pick<SellerCommissionGroup, 'paymentTotal' | 'percentOfCommissionPaid'> {
    const paymentTotal = payments.reduce((running, entry) => running + (entry.payment.amount ?? 0), 0);
    const commissionTotal = rows.reduce((running, row) => running + (row.commission?.amount ?? 0), 0);

    return {
      paymentTotal,
      percentOfCommissionPaid: commissionTotal <= 0 ? undefined : paymentTotal / commissionTotal,
    };
  }

  private buildSellerPayments(rows: CommissionWithSummary[]): SellerPaymentEntry[] {
    const payments: SellerPaymentEntry[] = [];
    for (const row of rows) {
      for (const payment of row.payments ?? []) {
        payments.push({ payment, sourceRow: row });
      }
    }
    return payments;
  }

  percentOfCommission(
    paymentAmount: number | undefined,
    commissionAmount: number | undefined,
  ): number | undefined {
    if (paymentAmount == null || commissionAmount == null || commissionAmount === 0) {
      return undefined;
    }
    return paymentAmount / commissionAmount;
  }

  formatDateOnly(date: Date | undefined): string {
    if (date == null) {
      return '—';
    }
    return formatDate(date, 'yyyy-MM-dd', 'en-US', 'UTC');
  }

  // ApprovedAt is an instant, not a calendar day, so it is rendered in the reader's own timezone —
  // unlike formatDateOnly/formatDisplayDate above, which pin a DateOnly to UTC to stop it drifting
  // a day.
  formatApprovalStamp(date: Date | undefined): string {
    if (date == null) {
      return '—';
    }
    return formatDate(date, 'MM/dd/yyyy h:mm a', 'en-US');
  }

  formatDisplayDate(date: Date | undefined): string {
    if (date == null) {
      return '—';
    }
    return formatDate(date, 'MM/dd/yyyy', 'en-US', 'UTC');
  }

  // Shared by the projectId effect and the change-order subscription so the flag gate exists once.
  // Reading enableCommissions() through a method call still registers as an effect dependency —
  // signal tracking follows dynamic scope, not lexical scope.
  private reloadCommissionsIfEnabled(): void {
    if (this.enableCommissions()) {
      this.loadCommissions();
    }
  }

  private loadCommissions(): void {
    this.commissionsError.set(false);
    this.commissionService
      .getByProject(this.projectId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (r) => {
          this.commissions.set(r);
          this.commissionsError.set(false);
        },
        error: () => {
          this.commissions.set([]);
          this.commissionsError.set(true);
        },
      });
    this.commissionService
      .getContext(this.projectId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (r) => this.commissionContext.set(r),
        error: () => this.commissionContext.set(undefined),
      });
  }

  private loadPpj(): void {
    this.ppjError.set(false);
    this.ppjService
      .getByProject(this.projectId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (r) => {
          this.ppj.set(r);
          this.ppjError.set(false);
        },
        error: () => {
          this.ppj.set(undefined);
          this.ppjError.set(true);
        },
      });
  }

  private loadPpjExceptions(): void {
    this.ppjExceptionsError.set(false);
    this.ppjService
      .getExceptions(this.projectId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (r) => {
          this.ppjExceptions.set(r);
          this.ppjExceptionsError.set(false);
        },
        error: () => {
          this.ppjExceptions.set([]);
          this.ppjExceptionsError.set(true);
        },
      });
  }

  private wireCommissionEditModal(modalRef: NgbModalRef): void {
    modalRef.componentInstance.saved.subscribe(() => {
      modalRef.close();
      this.onCommissionSaved();
    });
    modalRef.componentInstance.cancelled.subscribe(() => modalRef.dismiss());
  }
}
